import React, {SyntheticEvent} from "react";
import {autorun, runInAction} from "mobx";
import EsriError from "@arcgis/core/core/Error";

import {UiState} from "../../ui-state";

import {PrintConfiguration} from "../../config/print-configuration";
import {TopicConfiguration} from "../../../config/topic-configuration";
import {observer} from "mobx-react";
import "./printing.scss";
import TemplateOptions from "@arcgis/core/widgets/Print/TemplateOptions";
import {AppState} from "../../../components/app/app-mobx";
import _ from "lodash";
import PrintViewModel from "@arcgis/core/widgets/Print/PrintViewModel";
import {Form, Button, Select, Input, Message} from "semantic-ui-react";
import {Icon} from "../icon/icon";
import PrintTemplate from "@arcgis/core/rest/support/PrintTemplate";
import FileLink from "@arcgis/core/widgets/Print/FileLink";
import {PathUtils} from "../../../utils/path-utils";
import {Slider} from "react-semantic-ui-range";

interface PrintingProps {
    config: PrintConfiguration;
    view: __esri.MapView | __esri.SceneView | undefined;
    uiStore: UiState;
    appStore: AppState;
    small?: boolean;
    visible: boolean;
}

// https://github.com/Esri/arcgis-js-api/blob/bb6824315a3ebb0e23e5cfa7f3319127a4b5a7a6/widgets/Print.tsx
@observer
export class Printing extends React.Component<PrintingProps, any> {
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    print: any;
    printingRef: any;
    widgetKey = "printing";
    linkHelperElement: HTMLDivElement;
    previewElement: HTMLDivElement;
    constructor(props: PrintingProps) {
        super(props);
        this.state = {
            viewModel: null,
            exportLink: null,
            layout: "",
            subtopicLabel: "",
            screenExtent: null,
            printReady: false,
            printScale: null,
            printSize: null,
            rotation: 0,
            selectOpen: false,
            title: "",
            topicId: "",
            subtitle: "",
            isLoading: true,
            visible: this.props.visible
        };
        this.printingRef = React.createRef();
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
            this.linkHelperElement = document.createElement("div");
            this.linkHelperElement.className = "geo-link-helper";
            this.linkHelperElement.appendChild(document.createElement("div"));
            // Aufsteigend Sortiert
            this.props.config.scales.sort((a, b) => a-b);
        } else {
            this.previewElement = document.createElement("div");
            this.previewElement.className = "geo-print-preview";
            this.previewElement.appendChild(document.createElement("div"));
            // Absteigend Sortiert
            this.props.config.scales.sort((a, b) => b-a);
        }
        this.handlePrintMap = this.handlePrintMap.bind(this);
        this.handleFormatChange = this.handleFormatChange.bind(this);
        this.handleScaleChange = this.handleScaleChange.bind(this);
        this.handleSubtitleChange = this.handleSubtitleChange.bind(this);
        this.handleTitleChange = this.handleTitleChange.bind(this);
        this.handleSelectOpen = this.handleSelectOpen.bind(this);
        this.handleSelectClose = this.handleSelectClose.bind(this);
    }

    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((reaction: any) => {
            if (this.props.uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }
            const {view} = this.props;
            if (!view) {
                return;
            }
            this.loadPrintViewModel(view);
            const {config} = this.props;
            const {topic, subtopicLabel} = this.props.appStore;
            this.updateCustomTextElements(config, topic, subtopicLabel);

            this.props.uiStore.addWidget(this.widgetKey, this);
            this.setState({rotation: (view as __esri.MapView).rotation});
            reaction.dispose();
        }));
        this.autorunDisposers.push(autorun(() => {
            const {config} = this.props;
            const {topic, subtopicLabel} = this.props.appStore;
            this.updateCustomTextElements(config, topic, subtopicLabel);
        }));
    }
    updateCustomTextElements(config: PrintConfiguration, topic: TopicConfiguration, subtopicLabel?: string): void {
        const {viewModel} = this.state;
        if (!viewModel || !config.templateOptions) {
            return;
        }
        this.setCustomTextElements(config, topic, subtopicLabel);
    }
    setCustomTextElements(config: PrintConfiguration, topic: TopicConfiguration,
                          subtopicLabel?: string, watch?: any): void {
        if (topic.id === this.state.topicId && subtopicLabel === this.state.subtopicLabel){
            return;
        }
        const textElements = {};
        let count = 0;
        if (config.templateOptions?.customTextElements) {
            const {viewModel} = this.state;
            const customTextElements = config.templateOptions?.customTextElements.map((e) => {
                const element = {};
                for(const key in e) {
                    if (e.hasOwnProperty(key)){
                        element[key] = e[key];
                        element[key] = element[key].replace("{subtopic_label}", subtopicLabel);
                    }
                }
                return element;
            });
            for(const layout in viewModel.effectiveTemplateCustomTextElements){
                if (viewModel.effectiveTemplateCustomTextElements.hasOwnProperty(layout)) {
                    textElements[layout] = _.merge(viewModel.effectiveTemplateCustomTextElements[layout],
                        customTextElements);
                    count++;
                }
            }
            if (count > 0) {
                // Esri Dokumentation stimmt nicht mit Typefile überein!
                viewModel.templateCustomTextElements = textElements;
                this.setState({
                    subtopicLabel,
                    subtitle: subtopicLabel,
                    topicId: topic.id,
                    viewModel
                });
                if(watch){
                    watch.remove();
                }
            }
        }
    }
    calculatePreview(layout?: string, printScale?: number): void {
        const { config, small, view } = this.props;
        if(view) {
            this.setState({printReady: false});
            if (layout === undefined) {
                layout = this.state.layout;
            }
            if (!layout) {
                return;
            }
            const layoutDimension = config.layouts.get(layout);
            if (!layoutDimension) {
                return;
            }
            const maxPixel = [view.width-100, view.height-150];
            const maxMeters = [
                view.extent.width / view.width * maxPixel[0],
                view.extent.height / view.height * maxPixel[1]
            ];
            if (small) {
                // gesamte Breite in der mobilen Ansicht
                maxMeters[0] = view.extent.width;
            }
            if (printScale === undefined) {
                if(this.state.printScale && !small){
                    printScale = this.state.printScale;
                } else {
                    if (small) {
                        // Bei small wird ein Massstab gewählt, in welchem der ganze Extent Platz hat.
                        const maxScale = Math.max(
                            maxMeters[0] / layoutDimension[0], maxMeters[1] / layoutDimension[1]) * 1000;
                        printScale = config.scales.find((s) => s >= maxScale);
                    } else {
                        // Massstab damit der Druckbereich innerhalb des Extents ist.
                        const minScale = Math.min(
                            maxMeters[0] / layoutDimension[0], maxMeters[1] / layoutDimension[1]) * 1000;
                        printScale = config.scales.find((s) => s < minScale);
                    }
                }
            }
            if (!printScale) {
                printScale = config.scales[config.scales.length-1];
            }
            const printSize = [
                Math.round(layoutDimension[0] * printScale / 1000),
                Math.round(layoutDimension[1] * printScale / 1000)
            ];
            this.setState({printScale, printSize});
            if (this.previewElement) {
                const previewSize = [
                    Math.round(maxPixel[0] / (maxMeters[0] / layoutDimension[0] * 1000) * printScale),
                    Math.round(maxPixel[1] / (maxMeters[1] / layoutDimension[1] * 1000) * printScale)
                ];
                const previewContent = this.previewElement.getElementsByTagName("div")[0];
                if (previewContent) {
                    const borders = [Math.max((view.width - previewSize[0]) / 2, 0),
                        Math.max((view.height - previewSize[1]) / 2, 0)];
                    const uiTopOffset = view.height
                        - document.getElementsByClassName("esri-ui-manual-container")[0].clientHeight;
                    this.previewElement.style.top = (uiTopOffset * -1) + "px";
                    previewContent.style.borderLeftWidth = borders[0] + "px";
                    previewContent.style.borderRightWidth = borders[0] + "px";
                    previewContent.style.borderTopWidth = borders[1] + "px";
                    previewContent.style.borderBottomWidth = borders[1] + "px";
                }
            }
        }
    }
    componentDidUpdate(prevProps: PrintingProps) {
        if (this.props.visible !== prevProps.visible) {
            this.setState({visible: this.props.visible});
            if(this.props.view) {
                if (this.props.visible) {
                    this.watchHandles.push(this.props.view.watch("rotation", (newValue: number) => {
                        this.setState({
                            rotation: Math.round((newValue>180)? newValue-360: newValue),
                            printReady: false});
                    }));
                    this.watchHandles.push(this.props.view.watch(
                        "scale", () => {
                            this.calculatePreview();
                        }));
                    this.watchHandles.push(this.props.view.watch(
                        "size", () => {
                            this.calculatePreview();
                        }));
                    this.watchHandles.push(this.props.view.watch(
                        "center", () => {
                            if(this.state.printReady) {
                                this.setState({printReady: false});
                            }
                        }));
                    this.calculatePreview();
                    this.props.view.ui.add(this.previewElement,
                        "manual");
                } else {
                    this.setState({printScale: null});
                    // TODO: Nur watchHandle für extent entfernen
                    this.watchHandles.forEach((handle: any) => {
                        handle.remove();
                    });
                    this.watchHandles.length = 0;
                    this.props.view.ui.remove(this.previewElement);
                }
            }
        }
    }
    componentWillUnmount(): void {
        const {uiStore} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        if(this.previewElement){
            this.previewElement.remove();
        }
        if(this.linkHelperElement){
            this.linkHelperElement.remove();
        }
        uiStore.removeWidget(this.widgetKey);
    }
    createFileLink(template: PrintTemplate, fileName: string): __esri.FileLink {
        const titleText = fileName || this.state.title;
        const lowercaseFormat = template.format.toLowerCase();
        const extension = lowercaseFormat.indexOf("png") > -1 ? "png" : lowercaseFormat;

        return new FileLink({
            name: "GeoportalLU_" + PathUtils.slug(titleText) + "_" + new Date().toISOString().slice(0,10),
            extension,
            count: 0
        });
    }
    handlePrintMap(event?: SyntheticEvent<HTMLButtonElement>): void {
        const {config} = this.props;
        const {viewModel, layout} = this.state;
        if (event) {
            event.currentTarget.blur();
        }
        const template = new PrintTemplate({
            layoutOptions: {
                customTextElements: viewModel.templateCustomTextElements[layout],
                titleText: this.state.title
            },
            format: viewModel.templatesInfo.format.defaultValue,
            layout,
            outScale: this.state.printScale
        });
        if (config.templateOptions?.dpi) {
            template.exportOptions.dpi = parseInt(config.templateOptions?.dpi, 10);
        }
        template.layoutOptions.customTextElements?.find((e) => {
            if (e.hasOwnProperty("subtitle")){
                e.subtitle = this.state.subtitle;
                return true;
            }
        });
        const fileLink = this.createFileLink(template, this.state.title);
        this.setState({exportLink: fileLink, printReady: false});
        viewModel
            .print(template)
            .then((result: any) => {
                const link = document.createElement("a");
                let pdfUrl = config.exportDirectory + "/";
                pdfUrl += result.url.substring(result.url.lastIndexOf("_") + 1, result.url.lastIndexOf("."));
                pdfUrl = pdfUrl + "/" + this.state.exportLink.formattedName;
                link.href = pdfUrl;
                link.className = "geo-link-helper";
                link.setAttribute("target", "_blank");
                // link.setAttribute("download", this.state.exportLink.formattedName);
                if (this.linkHelperElement) {
                    this.linkHelperElement.appendChild(link);
                } else if (this.previewElement) {
                    this.previewElement.appendChild(link);
                }
                link.click();
                this.setState((state: any) => {
                    const {exportLink} = state;
                    exportLink.set({
                        url: pdfUrl,
                        state: "ready"
                    });
                    return {exportLink, printReady: true};
                });
            })
            .catch((error: EsriError) => {
                console.error(error);
                this.setState((state: any) => {
                    const {exportLink} = state;
                    exportLink.set({
                        error,
                        state: "error"
                    });
                    return { exportLink };
                });
            });
    }
    handleTitleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState(() => ({ title: event.target.value }));
    }
    handleSubtitleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState(() => ({ subtitle: event.target.value }));
    }
    handleFormatChange(event: SyntheticEvent, data: any): void {
        const {small} = this.props;
        const layout = data.value;
        this.setState(() => ({ layout }));
        this.calculatePreview(layout, (small)? undefined: this.state.printScale);
    }
    handleScaleChange(event: SyntheticEvent, data: any): void {
        const printScale = data.value;
        this.setState(() => ({ printScale }));
        this.calculatePreview(this.state.layout, printScale);
    }
    handleSelectOpen(): void {
        this.setState({selectOpen: true});
    }
    handleSelectClose(): void {
        this.setState({selectOpen: false});
    }
    handleRotationChange(rotation: number | string): void {
        const {view} = this.props;
        if(typeof rotation === "string"){
            rotation = parseInt(rotation, 10);
            if (!rotation) {
                rotation = 0;
            }
        }
        rotation = Math.min(Math.max(rotation, -180), 180);
        (view as __esri.MapView).rotation = rotation;
    }
    loadPrintViewModel(view: __esri.MapView | __esri.SceneView): void {
        const {appStore} = this.props;
        const config = this.props.config as __esri.PrintProperties;
        if (typeof config.templateOptions === "undefined") {
            config.templateOptions = new TemplateOptions();
        }
        config.templateOptions.title = appStore.topic.label;
        config.templateOptions.fileName = appStore.topic.id;
        if (typeof config.templateOptions.scaleEnabled === "undefined") {
            // Falls nicht definiert auf true setzten, damit das Widget den Massstab mitsendet.
            config.templateOptions.scaleEnabled = true;
        }
        const props = {
            view,
            ...config
        };
        const viewModel = new PrintViewModel(props as __esri.PrintViewModelProperties);
        viewModel.load().then(() => {
            runInAction(() => {
                this.setState({
                    viewModel,
                    title: appStore.topic.label,
                    layout: viewModel.templatesInfo.layout.defaultValue,
                    isLoading: false
                });
            });
            if (this.state.visible) {
                this.calculatePreview(viewModel.templatesInfo.layout.defaultValue);
            }
        });
    }

    render(): JSX.Element | null {
        const {config, small} = this.props;
        const {isLoading, exportLink, layout, printScale, printSize,
            rotation, selectOpen, subtitle, title, viewModel, visible} = this.state;
        if (!visible) {
            return null;
        }
        if (isLoading) {
            return <div className="geo-loading-wrapper"><Icon name={"recycle"} className={"geo-rotating"} /></div>;
        }
        const buttons: JSX.Element[] = [];
        let infoBlock: JSX.Element | null = null;
        if (exportLink) {
            if (exportLink.state==="pending") {
                buttons.push(<Button key={"print"} compact disabled size={"small"}><Icon name={"document_empty"} />
                    PDF wird erstellt <Icon name={"recycle"} className={"geo-rotating"} /></Button>);
            } else {
                buttons.push(<Button
                    key={"print"}
                    className={exportLink.state}
                    compact
                    primary={!this.state.printReady}
                    onClick={(e)=> this.handlePrintMap(e)}
                    size={"small"}><Icon name={"document_empty"} /> PDF erstellen{
                        (exportLink.state==="error")? <Icon name={"sign_warning"} />: ""}
                </Button>);
                if (exportLink.state==="ready") {
                    buttons.push(<Button
                        key={"download"}
                        compact
                        href={exportLink.url}
                        positive={this.state.printReady}
                        target={"_blank"}
                        rel={"noreferrer"}
                        size={"small"}><Icon name={"nav_down"} /> PDF öffnen</Button>);
                } else if (exportLink.state==="error") {
                    infoBlock = <Message
                        attached="bottom"
                        className={"error"}
                        content={"PDF erstellen ist fehlgeschlagen. Bitte versuchen Sie es später noch einmal."}
                    />;
                }
            }
        } else {
            buttons.push(<Button
                key={"print"}
                compact
                onClick={(e)=> this.handlePrintMap(e)}
                primary
                size={"small"}><Icon name={"document_empty"} /> PDF erstellen</Button>);
        }
        const sliderSettings = {
            start: rotation,
            min: -180,
            max: 180,
            step: 1,
            onChange: (value: number) => {
                this.handleRotationChange(value);
            }
        };
        return (
            <div className={"printWrapper" + (selectOpen? " geo-select-open": "")}>
                <Form size={"small"}>
                    <Form.Field>
                        <label>Titel <span>max. 30 Zeichen</span></label>
                        <Input
                            maxLength={30}
                            name="title"
                            onChange={this.handleTitleChange}
                            value={title} />
                    </Form.Field>
                    <Form.Field>
                        <label>Untertitel <span>max. 50 Zeichen</span></label>
                        <Input
                            maxLength={50}
                            name="subtitle"
                            onChange={this.handleSubtitleChange}
                            value={subtitle} />
                    </Form.Field>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Format</label>
                            <Select
                                fluid
                                name="layout"
                                onChange={this.handleFormatChange}
                                onOpen={this.handleSelectOpen}
                                onClose={this.handleSelectClose}
                                value={layout}
                                options={viewModel.templatesInfo.layout.choiceList.filter(
                                    (l: string) => config.layouts.has(l)).map((l: string) =>
                                    ({value: l, text: l.replace("_", " ")}))}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>Druckmassstab</label>
                            {small? (<div>1:{new Intl.NumberFormat("de-CH").format(printScale)}</div>): (
                                <Select
                                    fluid
                                    name="printScale"
                                    onChange={this.handleScaleChange}
                                    onOpen={this.handleSelectOpen}
                                    onClose={this.handleSelectClose}
                                    value={printScale}
                                    options={config.scales.map((s: number) =>
                                        ({value: s, text:"1:"+ new Intl.NumberFormat("de-CH").format(s)}))}
                                />)
                            }
                        </Form.Field>
                    </Form.Group>
                    <Form.Field inline>
                        <label>Ausschnitt</label>
                        {printSize? <span>{new Intl.NumberFormat("de-CH").format(printSize[0])
                        } x {new Intl.NumberFormat("de-CH").format(printSize[1])} m</span>: null}
                    </Form.Field>
                    <Form.Field className={"geo-slider-field"}>
                        <label>Rotation</label>
                        <Input
                            maxLength={4}
                            name="rotation"
                            onChange={(event) => {
                                this.handleRotationChange(event.target.value);
                            }}
                            min={-180}
                            max={180}
                            type={"number"}
                            value={rotation} />
                        <Slider
                            className="basemap-gallery-slider"
                            style={{ trackFill: { backgroundColor: "rgb(0, 159, 227)"}}}
                            value={rotation} settings={sliderSettings} />
                    </Form.Field>
                    {buttons}
                </Form>
                {infoBlock}
            </div>
        );
    }
}
